// components/VoteButton.tsx
import { Slider } from 'antd';
import { useAuth } from 'helpers/authContext';
import { getVoteValue, getVotingPower } from 'helpers/steem';
import React, { useEffect, useRef, useState } from 'react';
import {  postKeychainVote } from 'helpers/keychain';
import {  postWifVote } from 'helpers/steem';
import { getAccount, isLoggedWithKeychain, steemCustomApi } from 'services/dsteem-service';
import ActiveVotesButton from './ActiveVotesButton';
import { useToast } from './ui/use-toast';

type Props = { 
  position:string;
  post:any;
  author:string;
  permlink:string;
  reward_fund:any;
  median_price:any;
  setLocalReplies; 
  className?:string;
  onLogin: () => void; 
  onNewError:Function;
};

const getSavedVoteWeight = (username: string | null): number => {
  if (!username) return 100;
  try {
    const saved = localStorage.getItem(`vote_weight_${username.toLowerCase()}`);
    if (saved !== null) {
      const parsed = parseInt(saved, 10);
      if (!isNaN(parsed) && parsed >= 1 && parsed <= 100) {
        return parsed;
      }
    }
  } catch (e) {
    console.error("Error reading saved vote weight:", e);
  }
  return 100;
};

const VoteButton = ({ position, post, author, permlink ,reward_fund, median_price, setLocalReplies,
  onLogin, onNewError,className }: Props) => {
    const {toast} = useToast();
  const { user, autologin  } = useAuth(); 
  const [voteWeight, setVoteWeight] = useState<number>(() => getSavedVoteWeight(user));
  const [voteValue, setVoteValue] = useState(0.00);
  const [userSessionData, setUserSessionData] = useState(null);
  const [votingPower, setVotingPower] = useState(100);
  const [isVoting, setIsVoting] = useState(false);
  const [hasVoted, setHasVoted] = useState(false);
  const [votes, setVotes] = useState(0);
  const [open, setOpen] = useState(false);
  const [commentToVote, setCommentToVote] = useState<{ author: string | null; permlink: string | null }>({
    author: null,
    permlink: null,
  }); 
  const [localRewardFund, setLocalRewardFund] = useState(reward_fund);
  const [localMedianPrice, setLocalMedianPrice] = useState(median_price);

  useEffect(() => {
    setVoteWeight(getSavedVoteWeight(user));
  }, [user]);

  useEffect(() => {
    if (reward_fund) setLocalRewardFund(reward_fund);
    if (median_price) setLocalMedianPrice(median_price);
  }, [reward_fund, median_price]);

  useEffect(() => {
    const fetchMissingGlobals = async () => {
      if (!localRewardFund) {
        const rf = await steemCustomApi("condenser_api.get_reward_fund", ["post"]);
        if (rf) setLocalRewardFund(rf);
      }
      if (!localMedianPrice) {
        const mp = await steemCustomApi("condenser_api.get_current_median_history_price", []);
        if (mp) setLocalMedianPrice(mp);
      }
    };
    if (open) {
      fetchMissingGlobals();
    }
  }, [open, localRewardFund, localMedianPrice]);
 
  const handleClick = ( ) => {
    if (!user) {
      onLogin();
    } else { 
      handleVoteSubmit();
      setOpen(false);
    }
  };
  let keychainLogin = isLoggedWithKeychain(autologin); 
  const handleActualVote = async (weight, post_author, post_permlink) => { 
    setIsVoting(true);

    const voteFn = keychainLogin ? postKeychainVote : postWifVote;
    const voter = keychainLogin ? user : autologin;

    try {
      const result:any = await voteFn(post_author, post_permlink, weight, voter);

      if (!result.success) {
        onNewError(result.result?.message || "Error voting.");
        toast({
          title: "Error sending vote",
          description: result.result?.message,
          variant:"rose_red"
        }); 
        setIsVoting(false);
        return;
      }

      const isPost = post_author === author && post_permlink === permlink;

      toast({
        title: "Vote added successfully!",
        description: "You have voted for this post.",
        variant:"sky_blue"
      }); 

      if (isPost) { 
        setHasVoted(weight === 0 ? false : true);
        setVotes(prev => weight === 0 ? prev - 1 : prev + 1);
      } else { 
        setLocalReplies(prevReplies =>
          prevReplies.map(reply => {
            if (reply.author === author && reply.permlink === permlink) {
              const newVote = { voter: user, percent: weight * 100 }; 
              const updatedVotes = weight === 0
                ? reply.active_votes.filter(v => v.voter !== user)
                : [...(reply.active_votes || []).filter(v => v.voter !== user), newVote];

              return {
                ...reply,
                net_votes: weight === 0 ? reply.net_votes - 1 : reply.net_votes + 1,
                active_votes: updatedVotes,
                active_votes_modified: updatedVotes,
              };
            }
            return reply;
          })
        );
      } 
    } catch (error) { 
      onNewError("Error voting: " + error.message);
      toast({
        title: "Error sending vote",
        description: error.message,
        variant:"rose_red"
      }); 
    }
    setIsVoting(false);
  }; 
  const handleVoteWeightChange = (value: number) => {
      setVoteWeight(value);
      if (user) {
        try {
          localStorage.setItem(`vote_weight_${user.toLowerCase()}`, value.toString());
        } catch (e) {
          console.error("Error saving vote weight:", e);
        }
      }
      if (!userSessionData?.[0] || !localRewardFund || !localMedianPrice) return;
      const vp = getVotingPower(userSessionData[0]);
      setVotingPower(vp);
      setVoteValue(getVoteValue(userSessionData[0], vp, localRewardFund, localMedianPrice, value));
  };

  const handleVoteSubmit = () => {
    let weight = hasVoted? 0 : voteWeight;
    handleActualVote(weight, commentToVote.author , commentToVote.permlink );
    setCommentToVote({ author: null, permlink: null });
  };
  function getActiveVotes (post_data:any) {
    let active_votes = post_data.active_votes;
    if (post_data.net_votes > 0 && post_data.active_votes_modified) { 
      active_votes = post_data.active_votes_modified;
    }
    return active_votes;
  } 
  useEffect(() => {
    if (post && post.active_votes) { 
      let votes_counter = 0;
      let voted = false;
      let active_votes = getActiveVotes(post);
      for (let i = 0; i < active_votes.length; i++) {
        const voter = active_votes[i];  
        if (voter.percent > 0) {
          votes_counter++;
          if (voter.voter === user) {
            voted = true;
          }
        }
      } 
      setHasVoted(voted);
      setVotes(votes_counter); 
    } 
  }, [post, user]); 
  const fetchSessionData = async () => {
    if (user) {
        try {
            const data = await getAccount(user);
            setUserSessionData(data);
            if (userSessionData) {
              setVotingPower(getVotingPower(userSessionData[0]));
            }
            
        } catch (error) {
            onNewError("Error fetching user session data:" + error);
            toast({
              title: "Error with session user data.",
              description: "Your session is expired or corrupted, try starting it again.",
              variant:"rose_red"
            }); 
        }
    }
  }; 
  const menuRef = useRef<HTMLDivElement | null>(null); 
  useEffect(() => {
    if (!user) return;

    let mounted = true;
    const run = async () => { 
      await fetchSessionData();
    };
    run();

    const vp_refresh_interval = setInterval(() => {
      if (mounted) fetchSessionData();
    }, 30000);

    return () => {
      mounted = false;
      clearInterval(vp_refresh_interval);
    };
  }, [user]);
  useEffect(() => { 
    if (userSessionData?.[0] && localRewardFund && localMedianPrice) {
      const vp = getVotingPower(userSessionData[0]);
      setVotingPower(vp);
      setVoteValue(getVoteValue(userSessionData[0], vp, localRewardFund, localMedianPrice, voteWeight));
    }
  }, [userSessionData, localRewardFund, localMedianPrice, voteWeight]); 
  useEffect(() => {
    const handleClickOutside = (event) => {
    const menuNode = menuRef.current;
    if (menuNode && !menuNode.contains(event.target)) {
        setOpen(false);
    }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={menuRef} className="relative d-inline-block">
      {className?.includes("swipe-btn") && (
        <div className="swipe-action-item">
          <button
            className={`${hasVoted ? 'voted' : ''} ${className}`} 
            onClick={() => {
              if (user) {
                setOpen(!open);
                setCommentToVote({ author , permlink  });
              }else {
                onLogin();
              }
              
            }}
          >
            {!isVoting && <i className="fas fa-heart m-0"  />} {(isVoting) && <span className="spinner dark small m-0" />}
          </button>
          <div className="swipe-action-text">
            <ActiveVotesButton
              position='right'
              type='inline'
              post={post}
              onNewError={onNewError}
            />
          </div>
        </div>
      )}
      {!className?.includes("swipe-btn") && (
        <button
          className={`btn reply-votes ${hasVoted ? 'voted' : ''} ${className}`} 
          onClick={() => {
            if (user) {
              setOpen(!open);
              setCommentToVote({ author , permlink  });
            }else {
              onLogin();
            } 
          }}
        >
          <i className="fas fa-heart"  /> {(!isVoting) && (votes || 0) }{(isVoting) && <span className="spinner dark small" />}
        </button>
      )}
      
      {open && (
        <div className={"absolute vote-popup-window dropdown-window " + position}>
          {!hasVoted ? (
            <>
              {/* ── Header row: power + value ── */}
              <div className="vote-popup-header">
                {/* Voting Power ring */}
                <div className="vote-power-ring">
                  <svg viewBox="0 0 36 36" className="vote-ring-svg">
                    <circle cx="18" cy="18" r="15.9" className="vote-ring-bg" />
                    <circle
                      cx="18" cy="18" r="15.9"
                      className="vote-ring-fill"
                      strokeDasharray={`${votingPower} ${100 - votingPower}`}
                      strokeDashoffset="25"
                    />
                  </svg>
                  <span className="vote-ring-label">{votingPower.toFixed(0)}%</span>
                </div>
                <div className="vote-popup-stats">
                  <span className="vote-popup-label">Voting Power</span>
                  {/* Estimated value badge */}
                  <div className="vote-value-badge">
                    <i className="fas fa-dollar-sign" />
                    <span>{voteValue.toFixed(3)}</span>
                  </div>
                  <span className="vote-popup-label">Est. value</span>
                </div>
                {/* Weight big number */}
                <div className="vote-weight-display">
                  <span className="vote-weight-number">{voteWeight}</span>
                  <span className="vote-weight-unit">%</span>
                </div>
              </div>

              {/* ── Slider ── */}
              <div className="vote-slider-wrap">
                <Slider
                  min={1}
                  max={100}
                  onChange={handleVoteWeightChange}
                  value={voteWeight}
                  step={1}
                  tooltip={{ formatter: value => `${value}%` }}
                  trackStyle={{ backgroundColor: 'var(--speem)', height: 6, borderRadius: 3 }}
                  railStyle={{ backgroundColor: 'var(--speem-gray-dark)', height: 6, borderRadius: 3 }}
                  handleStyle={{ borderColor: 'var(--speem)', background: 'var(--speem)', height: 18, width: 18, marginTop: -6, boxShadow: '0 0 0 3px rgba(82,223,66,0.25)' }}
                />
                <div className="vote-slider-labels">
                  <span>1%</span>
                  <span>50%</span>
                  <span>100%</span>
                </div>
              </div>
            </>
          ) : (
            <div className="vote-unvote-confirm">
              <i className="fas fa-exclamation-triangle vote-warn-icon" />
              <p className="vote-warn-text">Remove your vote?</p>
              <p className="vote-warn-sub">Curation rewards will be lost.</p>
            </div>
          )}

          <button
            className={`vote-submit-btn w-100 mt-2 ${hasVoted ? 'unvote' : ''}`}
            onClick={() => { handleClick(); }}
            disabled={isVoting}
          >
            {isVoting
              ? <span className="spinner dark small" />
              : hasVoted
                ? <><i className="fas fa-heart-broken" /> Unvote</>
                : <><i className="fas fa-heart" /> Vote</>
            }
          </button>
        </div>
      )}

    </div>
    
  );
};

export default VoteButton;
